import { useCallback, useState } from "react";
import { useProperty, useToast } from "../provider";
import { UUIDHelper } from "@/src/util";
import { LeadBody } from "@/src/type/contact";
import { DynamicFormLogic, TrackingLogic } from "@/src/logic";
import { APP_TENANT, IS_LOCALHOST } from "@/src/const";
import { FrontendTrackingService } from "@/src/service";
import { FrontendLeadService } from "@/src/service";
import { StrapiComponentType } from "@/src/type";
import { DataObject, FormPropertyType } from "@/src/logic/DynamicFormLogic";
import { Destination } from "@/src/type/strapi/collection/Destination";

type DynamicFormProps = {
  metadata?: Record<string, string>;
  onSuccess?: (leadBody: LeadBody) => void;
  form?: StrapiComponentType.Section.DynamicForm;
  destinations?: Destination[];
  trackingEvents?: StrapiComponentType.Object.TrackingEvent[];
};

type SubmitFormProps = {
  formData: Record<string, any>;
  currentStep: number;
  extraMetadata?: Record<string, string>;
  extraFormProperties?: FormPropertyType[];
  trackingEventOverrides?: StrapiComponentType.Object.TrackingEvent[];
};

type UseDynamicFormResponse = {
  currentStepIndex: number;
  crmLinkId: string;
  isLoading: boolean;
  submitForm: (values: SubmitFormProps) => Promise<void>;
  openMultiStepModal: boolean;
  setOpenMultiStepModal: (open: boolean) => void;
};

export const useDynamicForm = ({ metadata, onSuccess, form, destinations, trackingEvents }: DynamicFormProps): UseDynamicFormResponse => {
  const toast = useToast();
  const { property } = useProperty();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openMultiStepModal, setOpenMultiStepModal] = useState<boolean>(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [dataObject, setDataObject] = useState<DataObject | undefined>(undefined);
  const [crmLinkId] = useState<string>(UUIDHelper.createUUID(true));

  const submitForm = useCallback(
    async ({ formData, currentStep, extraMetadata, extraFormProperties, trackingEventOverrides }: SubmitFormProps) => {
      setIsLoading(true);
      const data = DynamicFormLogic.createDataObjectData({ formData, dataObject, currentStep, form });
      if (currentStep === 0) {
        const _metadata = metadata
          ? Object.entries(metadata).map(([key, value]) => ({
              key,
              value,
              name: `${key}:${value}`,
            }))
          : [];
        if (extraMetadata) {
          _metadata.push(
            ...Object.entries(extraMetadata).map(([key, value]) => ({
              key,
              value,
              name: `${key}:${value}`,
            }))
          );
        }
        if (property?.slug) {
          _metadata.push({
            key: "propertySlug",
            value: property.slug,
            name: `propertySlug:${property.slug}`,
          });
        }
        _metadata.push({
          key: "crmLinkId",
          value: crmLinkId,
          name: `crmLinkId:${crmLinkId}`,
        });
        _metadata.push({
          key: "domain",
          value: window.location.hostname,
          name: `domain:${window.location.hostname}`,
        });
        _metadata.push({
          key: "pathname",
          value: window.location.pathname,
          name: `pathname:${window.location.pathname}`,
        });
        data.metadata = _metadata;
      }

      if (extraFormProperties) {
        data.formProperties = [...(data.formProperties ?? []), ...extraFormProperties];
      }

      const trackingParams = TrackingLogic.getUTMParams();
      const toSubmitForm: LeadBody = {
        id: dataObject?.id,
        form: {
          ...data,
          source: APP_TENANT,
          tracking: {
            referrer: trackingParams?.referrer,
            utmSource: trackingParams?.utmSource,
            utmMedium: trackingParams?.utmMedium,
            utmCampaign: trackingParams?.utmCampaign,
            utmContent: trackingParams?.utmContent,
            utmTerm: trackingParams?.utmTerm,
            sessionId: await TrackingLogic.getSessionId(),
            googleCLID: trackingParams?.googleCLID,
            facebookCLID: trackingParams?.facebookCLID,
            persistentId: TrackingLogic.getPersistentId(),
          },
        },
        rawForm: formData,
        fub: {
          campaign: {
            source: trackingParams?.utmSource,
            campaign: trackingParams?.utmCampaign,
            content: trackingParams?.utmContent,
            term: trackingParams?.utmTerm,
            medium: trackingParams?.utmMedium,
          },
          pageTitle: document.title,
          pageUrl: window.location.href,
          pageReferrer: trackingParams?.referrer,
        },
        destinations: destinations,
        otherData: {
          propertyName: property?.name,
          path: window.location.pathname,
          marketingSource: TrackingLogic.getMarketingSource(),
        },
        trackingParams: TrackingLogic.getTrackingParams(),
      };

      //to test without submitting to CRM
      //const formSubmit: any = { success: true };
      const formSubmit = dataObject?.id ? await FrontendLeadService.putLeadForm(toSubmitForm) : await FrontendLeadService.postLeadForm(toSubmitForm);

      if (formSubmit.success) {
        if (typeof onSuccess === "function") {
          onSuccess(toSubmitForm);
        }

        const leadId = formSubmit?.response?.payload?.data?.id;
        const _trackingEvents = trackingEventOverrides ?? trackingEvents;
        const _action = Array.isArray(_trackingEvents) && _trackingEvents.length > 0 ? _trackingEvents[0].gaEventName : "form_submit";
        if (!IS_LOCALHOST) {
          FrontendTrackingService.postTrackingLog({
            action: _action,
            actionId: crmLinkId,
            actionIdType: "crmLinkId",
            actionId2: form?.id,
            actionId2Type: "FormType",
            actionMessage: `useDynamicForm::Step::${currentStep}`,
            triggeredFrom: "useDynamicForm.tsx",
            trackingEvents: trackingEventOverrides ?? trackingEvents,
          });
        }

        toast.addMessage({
          title: form?.steps?.[currentStep]?.toastSuccessTitle ?? "We've captured your message",
          subtitle: form?.steps?.[currentStep]?.toastSuccessSubtitle ?? "",
          type: "success",
        });

        const nextStep = currentStep + 1;
        if (nextStep < (form?.steps?.length ?? 0)) {
          setCurrentStepIndex(nextStep);
          setOpenMultiStepModal(true);
        }

        if (currentStep === (form?.steps?.length ?? 0) - 1) {
          setOpenMultiStepModal(false);
        }

        setDataObject({
          id: leadId,
          data: data,
          lastResponse: formSubmit?.response?.payload,
        });
      } else {
        toast.addMessage({
          title: "Ughhh - an issue happened",
          subtitle: "We weren't able to save your message, give us a minute and try again.",
          type: "error",
        });
      }
      setIsLoading(false);
    },
    [crmLinkId, dataObject, form, toast, onSuccess, metadata, destinations, property, trackingEvents]
  );

  return { currentStepIndex, crmLinkId, isLoading, submitForm, openMultiStepModal, setOpenMultiStepModal };
};
